/**
 * A service, as the Services page edits it: a `HealthcareService` that AKB
 * wrote, the ones with a `FHIR_IDENTIFIER_SYSTEMS.HEALTHCARE_SERVICE`
 * identifier. This file holds only data description, no runtime logic.
 *
 * Not here: the locations a service is offered at, its appointment type and
 * duration, and the consent and scheduling extensions. They are cross-links
 * or scheduling data, and keep the values AKB wrote.
 */
import ClipboardList from "lucide-react/dist/esm/icons/clipboard-list";

import type { ConfigSchema } from "@/types/clinic-configuration";
import {
  FHIR_EXTENSIONS,
  FHIR_IDENTIFIER_SYSTEMS,
} from "@/constants/fhir.constants";
import { ROUTES } from "@/constants/routes.constants";

/**
 * Needs for i18next-scanner
 */
// t('Services')
// t('Name')
// t('Description')
// t('What Kora tells patients the service is')
// t('Minimum price')
// t('Maximum price')
// t('Currency')
// t('Results')
// t('How long until the results are ready')
// t('Prerequisites')
// t('What the patient must do beforehand')
// t('Service ID')
export const healthcareServiceSchema = {
  fields: {
    name: {
      kind: "text",
      path: "name",
      title: "Name",
      required: true,
      orientation: "horizontal",
      appearance: "standard",
    },
    description: {
      kind: "text",
      path: "comment",
      title: "Description",
      description: "What Kora tells patients the service is",
      layout: "stacked",
    },
    serviceId: {
      kind: "text",
      path: "identifier.value",
      match: { system: FHIR_IDENTIFIER_SYSTEMS.HEALTHCARE_SERVICE },
      title: "Service ID",
      readonly: true,
    },
  },
  extensions: {
    priceMin: {
      kind: "text",
      slug: FHIR_EXTENSIONS.HEALTHCARE_SERVICE_PRICE_MINIMUM,
      valueType: "string",
      title: "Minimum price",
      orientation: "horizontal",
      appearance: "standard",
    },
    priceMax: {
      kind: "text",
      slug: FHIR_EXTENSIONS.HEALTHCARE_SERVICE_PRICE_MAXIMUM,
      valueType: "string",
      title: "Maximum price",
      orientation: "horizontal",
      appearance: "standard",
    },
    currency: {
      kind: "text",
      slug: FHIR_EXTENSIONS.HEALTHCARE_SERVICE_CURRENCY,
      valueType: "string",
      title: "Currency",
      orientation: "horizontal",
      appearance: "standard",
    },
    results: {
      kind: "text",
      slug: FHIR_EXTENSIONS.HEALTHCARE_SERVICE_RESULTS_LEAD_TIME,
      valueType: "string",
      title: "Results",
      description: "How long until the results are ready",
      layout: "stacked",
    },
    prerequisites: {
      kind: "text",
      slug: FHIR_EXTENSIONS.HEALTHCARE_SERVICE_PREREQUISITES,
      valueType: "string",
      title: "Prerequisites",
      description: "What the patient must do beforehand",
      layout: "stacked",
    },
  },
} satisfies ConfigSchema.Resource;

export const servicesPage = {
  to: ROUTES.CLINIC_SERVICES,
  label: "Services",
  icon: ClipboardList,
  count: "serviceCount",
} satisfies ConfigSchema.Page;
